import { useUnit } from 'effector-react';
import { PanelLeft, PanelRight, Search } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { $marketStatus } from '../../entities/market';
import { PreferencesMenu } from '../../features/preferences-menu';
import { formatTime } from '../../shared/lib/format';
import { useLocale } from '../../shared/lib/i18n/use-locale';
import { shortcutOf } from '../../shared/lib/shortcuts';
import { IconButton, Mark, Pill, Shortcut, StatusDot, Tooltip } from '../../shared/ui';
import { paletteOpened } from '../command-palette';

export interface TopBarProps {
  crumbs: readonly ReactNode[];
  onToggleSidebar: () => void;
  onToggleInspector?: (() => void) | undefined;
}

export function TopBar({ crumbs, onToggleSidebar, onToggleInspector }: TopBarProps) {
  const { t } = useTranslation();
  const openPalette = useUnit(paletteOpened);
  return (
    <header className="flex h-11 shrink-0 items-center gap-3 border-b border-border-subtle bg-bg-1 px-2">
      <div className="flex min-w-0 items-center gap-1">
        <Tooltip content={t('shell.toggleSidebar')} shortcut={shortcutOf('toggleSidebar')}>
          <IconButton label={t('shell.toggleSidebar')} onClick={onToggleSidebar}>
            <PanelLeft />
          </IconButton>
        </Tooltip>
        <Mark className="mx-1" />
        <nav
          aria-label={t('shell.breadcrumb')}
          className="flex min-w-0 items-center gap-1.5 text-sm"
        >
          {crumbs.map((crumb, index) => (
            <Fragment key={index}>
              {index > 0 && (
                <span aria-hidden className="text-text-4">
                  /
                </span>
              )}
              <span
                className={
                  index === crumbs.length - 1
                    ? 'truncate font-medium text-text-1'
                    : 'truncate text-text-3'
                }
                aria-current={index === crumbs.length - 1 ? 'page' : undefined}
              >
                {crumb}
              </span>
            </Fragment>
          ))}
        </nav>
      </div>
      <div className="flex-1" />
      <MarketStatusPill />
      <button
        type="button"
        onClick={() => {
          openPalette();
        }}
        className="hidden h-7 items-center gap-2 rounded-md border border-border-strong bg-bg-2 pr-1 pl-2 text-sm text-text-3 outline-none hover:bg-bg-3 hover:text-text-1 focus-visible:ring-2 focus-visible:ring-focus sm:flex"
      >
        <Search className="size-4" aria-hidden />
        <span>{t('shell.search')}</span>
        <Shortcut keys={shortcutOf('palette')} />
      </button>
      <IconButton
        label={t('shell.search')}
        className="sm:hidden"
        onClick={() => {
          openPalette();
        }}
      >
        <Search />
      </IconButton>
      <PreferencesMenu />
      {onToggleInspector && (
        <Tooltip content={t('shell.toggleInspector')} shortcut={shortcutOf('toggleInspector')}>
          <IconButton
            label={t('shell.toggleInspector')}
            onClick={onToggleInspector}
            className="max-lg:hidden"
          >
            <PanelRight />
          </IconButton>
        </Tooltip>
      )}
    </header>
  );
}

function MarketStatusPill() {
  const { t } = useTranslation();
  const locale = useLocale();
  const status = useUnit($marketStatus);
  if (!status) return null;
  const hint = status.is_open
    ? t('market.closesAt', { time: status.closes_at ? formatTime(status.closes_at, locale) : '—' })
    : t('market.opensAt', { time: formatTime(status.next_open, locale) });
  return (
    <Tooltip content={hint}>
      <span className="max-md:hidden">
        <Pill icon={<StatusDot status={status.is_open ? 'good' : 'critical'} />}>
          {t(status.is_open ? 'market.open' : 'market.closed', { exchange: 'Xetra' })}
        </Pill>
      </span>
    </Tooltip>
  );
}
